import React, { useState, useEffect } from "react";
import { useMsal } from "@azure/msal-react";
import { useTranslation } from "react-i18next";
import { getAccessToken, getUserData } from "../../../Services/GraphServices";
import { retrieveCallRecordingUrl } from "../../../Services/HeedifyServices";
import { ArrowDownload20Filled } from "@fluentui/react-icons";
import {
  Tooltip,
  makeStyles,
  Button,
  TableBody,
  TableCell,
  TableRow,
  Spinner,
  TableCellLayout,
  Drawer,
  DrawerHeader,
  DrawerHeaderTitle,
  DrawerBody,
  DrawerFooter,
} from "@fluentui/react-components";
import { PlayCircleRegular, AddSquare28Regular, Dismiss24Regular } from "@fluentui/react-icons";
import "./CSS/TableBodyComponent.css";
const useStyles = makeStyles({
  container: {
    "> div": { paddingTop: "50px" },
  },
  contentHeader: {
    marginTop: "0",
  },
  truncatedText: {
    overflowX: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
});

export default function TableBodyComponent({
  data,
  setIsAudioPlayed,
  isAudioPlayed,
  setFilteredItems,
  currentRows,
  columnSizing_unstable,
  tenantId,
  columns = []
}) {
  
  const { t } = useTranslation();
  const authContext = useMsal();
  const { accounts, instance } = authContext;
  const activeAccount = accounts[0];
  const styles = useStyles();
  const [audioUrls, setAudioUrls] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedTranscription, setSelectedTranscription] = useState("");

  const handleOpenDrawer = (transcriptionText) => {
    setSelectedTranscription(transcriptionText);
    setIsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedTranscription("");
  };

  //fetch user name
  const fetchUserName = (callerId) => {
    return getAccessToken(instance, activeAccount)
      .then((accessToken) => getUserData(accessToken, callerId))
      .then((userData) => userData?.displayName || t("USER_NOT_FOUND")) // Vérification si `userData` contient un nom
      .catch((error) => {
        console.error(
          `Error fetching the name for the user ${callerId}:`,
          error
        );
        return t("USER_NOT_FOUND"); // Message d'erreur par défaut
      });
  };

  //get url audio
  const handlePlayAudio = (callId, recordingScope) => {
    retrieveCallRecordingUrl(tenantId, recordingScope, callId)
      .then((sasUrl) => {
        setAudioUrls((prev) => ({ ...prev, [callId]: sasUrl })); // Utilise callId comme clé
        setIsAudioPlayed((prev) => ({ ...prev, [callId]: true })); // Utilise callId
      })
      .catch((error) => {
        console.error("Erreur lors de la récupération de l'audio :", error);
      });
  };

  const filterAndMapData = (data) => {
    // 1. Transformer les données en tableau et trier par date décroissante
    const sortedData = Object.values(data).sort((a, b) => {
      return new Date(b.T_CALL_ANSWERED) - new Date(a.T_CALL_ANSWERED); // du plus récent au plus ancien
    });

    // 2. Mapper les enregistrements triés
    const promises = sortedData.map((recording, index) => {
      const callerLabelPromise =
        recording.CALLER_TYPE === "phone"
          ? Promise.resolve(recording.CALLER_ID) // Résoudre immédiatement avec la valeur disponible
          : fetchUserName(recording.CALLER_ID); // Appeler une fonction qui retourne une promesse

      return callerLabelPromise.then((callerLabel) => ({
        Recording: {
          icon: (
            <PlayCircleRegular
              className="play-icon"
              onClick={() =>
                handlePlayAudio(recording.RowKey, recording.PartitionKey)
              } // utilise callId
            />
          ),
        },
        Direction: { label: recording.DIRECTION || "" },
        recordingScope: {
          label: recording.FLOW_NAME || recording.AGENT_DISPLAYNAME || "",
        },
        callerId: { label: callerLabel || "" },
        duration: { label: `${recording.CALL_HANDLE_TIME} ` },
        agentName: { label: recording.AGENT_DISPLAYNAME || "" },
        date: { label: recording.T_CALL_ANSWERED || "" },
        callTrancription: {
          label:
            recording.isTranscriptionActive && recording.transcriptionText
              ? recording.transcriptionText
              : "",
          showPopover: recording.isTranscriptionActive,
        },
        callId: { label: recording.RowKey || "" },
      }));
    });

    return Promise.all(promises);
  };

  useEffect(() => {
    if (data) {
      setIsLoading(true);
      filterAndMapData(data)
        .then((newFilteredItems) => {
          setFilteredItems(newFilteredItems);
          setIsLoading(false);
        })
        .catch((error) => {
          console.error("Error while filtering and mapping data:", error);
          setIsLoading(false);
        });
    }
  }, [data]);

  //format duration
  const formatTime = (seconds) => {
    if (!seconds || seconds <= 0) return "00:00:00";
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    return `${hrs.toString().padStart(2, "0")}:${mins
      .toString()
      .padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };
  // Format the date dd/mm/yy

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp * 1000); // Convert seconds to milliseconds

    const options = {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    };

    return date.toLocaleString(navigator.language, options).replace(",", "");
  };
  //pour faire la traduction du valeur dans le dropdown
  const getTranslatedDirection = (item) => {
    switch (item) {
      case "IN":
        return t("IN");
      case "OUT":
        return t("OUT");
      default:
        return "";
    }
  };

  // Helper function to check if column is visible
  const isColumnVisible = (columnKey) => {
    if (!columns || columns.length === 0) return true;
    const column = columns.find(col => col.key === columnKey);
    return column ? column.visible : true;
  };

  // Get column order index
  const getColumnOrder = (columnKey) => {
    if (!columns || columns.length === 0) return 999;
    const index = columns.findIndex(col => col.key === columnKey);
    return index !== -1 ? index : 999;
  };

  // Sort columns by configured order
  const sortedColumns = [
    { key: 'audio', component: 'audio' },
    { key: 'direction', component: 'direction' },
    { key: 'recordingScope', component: 'recordingScope' },
    { key: 'callerId', component: 'callerId' },
    { key: 'duration', component: 'duration' },
    { key: 'agentName', component: 'agentName' },
    { key: 'date', component: 'date' },
    { key: 'transcription', component: 'transcription' },
    { key: 'callId', component: 'callId' },
  ]
    .filter(col => isColumnVisible(col.key))
    .map(col => ({ ...col, order: getColumnOrder(col.key) }))
    .sort((a, b) => a.order - b.order);

  return (
    <div className="table-body-container">
      {isLoading ? (
        <div className={styles.container}>
          <Spinner appearance="primary" />
        </div>
      ) : (
        <TableBody>
          {currentRows.map(({ item }) => (
            <TableRow key={item.callId.label}>
              {sortedColumns.map(({ key, component }) => {
                switch (component) {
                  case 'audio':
                    return (
                      <TableCell
                        key="audio"
                        {...columnSizing_unstable.getTableCellProps("Recording")}
                        className={` ${isAudioPlayed ? "audio-cell" : ""}`}
                      >
                        <TableCellLayout>
                          {isAudioPlayed[item.callId.label] ? (
                            <>
                              <audio
                                controls
                                controlsList="nodownload noplaybackrate"
                                onContextMenu={(e) => e.preventDefault()}
                              >
                                <source
                                  src={audioUrls[item.callId.label]}
                                  type="audio/mpeg"
                                />
                              </audio>
                              <a
                                href={audioUrls[item.callId.label]}
                                download={`audio-${item.callId.label}.mp3`}
                                className="download-icon"
                              >
                                <Tooltip withArrow content={t("DOWNLOAD")}>
                                  <ArrowDownload20Filled fontSize={28} />
                                </Tooltip>
                              </a>
                            </>
                          ) : (
                            <TableCellLayout media={item.Recording.icon} />
                          )}
                        </TableCellLayout>
                      </TableCell>
                    );

                  case 'direction':
                    return (
                      <TableCell
                        key="direction"
                        className=" body-text"
                        {...columnSizing_unstable.getTableCellProps("Direction")}
                      >
                        <TableCellLayout truncate>
                          {getTranslatedDirection(item.Direction.label)}
                        </TableCellLayout>
                      </TableCell>
                    );

                  case 'recordingScope':
                    return (
                      <TableCell
                        key="recordingScope"
                        className=" body-text"
                        {...columnSizing_unstable.getTableCellProps("recordingScope")}
                      >
                        <TableCellLayout truncate>
                          {item.recordingScope.label}
                        </TableCellLayout>
                      </TableCell>
                    );

                  case 'callerId':
                    return (
                      <TableCell
                        key="callerId"
                        className=" body-text"
                        {...columnSizing_unstable.getTableCellProps("callerId")}
                      >
                        <TableCellLayout truncate>
                          {item.callerId.label}
                        </TableCellLayout>
                      </TableCell>
                    );

                  case 'duration':
                    return (
                      <TableCell
                        key="duration"
                        className=" body-text"
                        {...columnSizing_unstable.getTableCellProps("duration")}
                      >
                        <TableCellLayout truncate>
                          {formatTime(item.duration.label)}
                        </TableCellLayout>
                      </TableCell>
                    );

                  case 'agentName':
                    return (
                      <TableCell
                        key="agentName"
                        className=" body-text"
                        {...columnSizing_unstable.getTableCellProps("agentName")}
                      >
                        <TableCellLayout truncate>
                          {item.agentName.label}
                        </TableCellLayout>
                      </TableCell>
                    );

                  case 'date':
                    return (
                      <TableCell
                        key="date"
                        {...columnSizing_unstable.getTableCellProps("date")}
                        className={`body-text ${isAudioPlayed ? "date-cell" : ""}`}
                      >
                        <TableCellLayout truncate>
                          {formatTimestamp(item.date.label)}
                        </TableCellLayout>
                      </TableCell>
                    );

                  case 'transcription':
                    return (
                      <TableCell
                        key="transcription"
                        className="body-text"
                        {...columnSizing_unstable.getTableCellProps("callTrancription")}
                      >
                        <TableCellLayout truncate>
                          <div
                            className={`table-cell-container ${
                              item.callTrancription.showPopover
                                ? "truncated-text"
                                : "full-text"
                            }`}
                          >
                            <span
                              className={
                                item.callTrancription.showPopover
                                  ? "truncated-text"
                                  : "full-text"
                              }
                            >
                              {item.callTrancription.label}
                            </span>
                            {item.callTrancription.showPopover && (
                              <Tooltip withArrow content={t("MORE")}>
                                <AddSquare28Regular 
                                  className="more-icon" 
                                  onClick={() => handleOpenDrawer(item.callTrancription.label)}
                                />
                              </Tooltip>
                            )}
                          </div>
                        </TableCellLayout>
                      </TableCell>
                    );

                  case 'callId':
                    return (
                      <TableCell key="callId" className={`body-text call_id_cell `}>
                        <Tooltip withArrow content={item.callId.label}>
                          <TableCellLayout truncate>
                            {item.callId.label}
                          </TableCellLayout>
                        </Tooltip>
                      </TableCell>
                    );

                  default:
                    return null;
                }
              })}
            </TableRow>
          ))}
        </TableBody>
      )}

      <Drawer
        type="overlay"
        position="end"
        open={isDrawerOpen}
        onOpenChange={(_, { open }) => setIsDrawerOpen(open)}
        size="small"
        className="transcription-drawer"
      >
        <DrawerHeader>
          <DrawerHeaderTitle
            action={
              <Button
                appearance="subtle"
                icon={<Dismiss24Regular />}
                onClick={handleCloseDrawer}
              />
            }
          >
            {t("TRANSCRIPTION")}
          </DrawerHeaderTitle>
        </DrawerHeader>
        <DrawerBody>
          <div className="transcription-drawer-body">
            {selectedTranscription}
          </div>
        </DrawerBody>
      </Drawer>
    </div>
  );
}
